import React, { PureComponent } from 'react';
import { arrayOf, array, func, object, shape } from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import {
  Card,
  BarChart,
  Button,
  ChartEmptyState,
  Databoxes,
  DoughnutChart,
} from 'common/components';
import { RevenueForm } from 'containers';
import { toCommaNumber } from 'common/formatters';
import { ROUTE_FIELDS } from 'common/constants';

const emptyStateTextForCrops = {
  chartTitle: 'Revenue Forecast',
  title: 'You have not added any crops yet.',
  subTitle: 'start adding crops to your fields.',
  buttonText: 'Add Crops',
};

const emptyStateForPrices = {
  chartTitle: 'Revenue Forecast',
  title: 'Revenue forecast not available.',
  subTitle: 'Add the expected prices to the crops',
  buttonText: 'Add Crop Prices',
};

/**
 *
 *
 * @export
 * @class RevenueChart
 * @extends {PureComponent}
 */
export class RevenueChart extends PureComponent {
  static displayName = 'RevenueChart';

  static propTypes = {
    data: shape({
      getRevenueForecast: shape({
        contracted: array,
        deficit: array,
        surplus: array,
      }),
      getAllCropsForField: arrayOf(object),
    }),
    goToFieldPage: func,
  };

  static defaultProps = {};

  state = { showRevenueChart: true };

  /**
   * @function renderChartView
   * @memberof RevenueChart
   * @returns {React.Dom} - <LineChart /> View
   */
  renderChartView = () => {
    const { data } = this.props;
    const { contracted, surplus } = data.getRevenueForecast;
    const totalRevenue = Math.round(
      this.getTotalRevenue((contracted || []).concat(surplus))
    );
    // filter out the data that has 0 or negative value for revenue doughnut chart
    const revenueByCrops = this.getRevenueByCrops(
      (contracted || []).concat(surplus)
    ).filter(revenueData => revenueData.y > 0);

    return (
      <Card>
        <div className="columns is-desktop">
          <div className="column is-one-third-desktop is-one-quarter-widescreen">
            <h2 className="text--header text--medium">Revenue Forecast</h2>
          </div>
          <div className="column is-two-thirds-desktop is-three-quarters-widescreen">
            <Databoxes
              data={[
                {
                  label: 'Total Expected Revenue',
                  line: { output: `$${toCommaNumber(totalRevenue)}` },
                },
              ]}
              theme={['tight']}
            />
          </div>
        </div>
        <div className="columns is-desktop">
          <div className="column is-one-third-desktop is-one-quarter-widescreen">
            <h5>Revenue by crop</h5>
            <DoughnutChart data={revenueByCrops} />
          </div>

          <div className="column is-two-thirds-desktop is-three-quarters-widescreen">
            <h5>Crop revenue contracted / uncontracted</h5>
            <BarChart toggleChart={this._toggelRevenueChart} data={data} />
          </div>
        </div>

        <footer className="field__group columns columns--reverse">
          <Button
            type="button"
            className="btn"
            onClick={this._toggelRevenueChart}
          >
            Update Spot Price
          </Button>
          <p className="column">
            <strong className="text--fade">
              Disclaimer: Calculations are based on expected harvest and
              contracted grain. Grain in storage is not included.
            </strong>
          </p>
        </footer>
      </Card>
    );
  };

  /**
   * @function renderFormView
   * @memberof RevenueChart
   * @returns {React.Dom} - <YieldsForm /> View
   */
  renderFormView = () => {
    const {
      data: { getAllCropsForField },
    } = this.props;
    return (
      <Card
        header={
          <h2 className="text--header text--medium">Expected Crop Prices</h2>
        }
        theme={['noborder']}
      >
        <div className="column is-one-third is-offset-one-third">
          <RevenueForm
            crops={getAllCropsForField}
            onSubmit={this._toggelRevenueChart}
            onCancel={this._toggelRevenueChart}
          />
        </div>
      </Card>
    );
  };

  render() {
    const { data, goToFieldPage } = this.props;
    const { contracted, surplus } = data.getRevenueForecast;

    if (!this.state.showRevenueChart) {
      return this.renderFormView();
    }

    if (!data.getAllCropsForField || !data.getAllCropsForField.length) {
      return (
        <ChartEmptyState
          {...emptyStateTextForCrops}
          navigateTo={goToFieldPage}
        />
      );
    }

    if (
      (!this.someValuesNotNull(contracted) &&
        !this.someValuesNotNull(surplus)) ||
      !this.somePricesExist(data.getAllCropsForField)
    ) {
      return (
        <ChartEmptyState
          {...emptyStateForPrices}
          navigateTo={this._toggelRevenueChart}
        />
      );
    }

    return this.renderChartView();
  }

  /**
   *
   * Returns true if some 'value' keys in the array are null
   * @function someValuesNotNull
   * @memberof RevenueChart
   * @param {array}
   * @returns {boolean}
   */
  someValuesNotNull = array => array.some(({ value }) => value !== null);

  /**
   *
   * Returns true if some 'expectedPrice' keys in the array are null
   * @function somePricesExist
   * @memberof RevenueChart
   * @param {array}
   * @returns {boolean}
   */
  somePricesExist = array =>
    array.some(({ expectedPrice }) => expectedPrice !== null);

  /**
   *
   *
   * @function getTotalRevenue
   * @returns {number}
   * @param {array} - ...surplus, ...contracted
   */
  getTotalRevenue = arr =>
    arr.reduce((sum, current) => Number(sum) + Number(current.value), 0);

  /**
   *
   *
   * @function getRevenueByCrops
   * @returns {number}
   * @param {array} - ...surplus, ...contracted, ...dificit
   * @returns {array} - in mock.doughnutChartData format
   */
  getRevenueByCrops = arr => {
    let data = {};
    arr.forEach(crop => {
      if (!data[crop.label]) {
        data[crop.label] = { value: 0 };
      }
      data[crop.label].value += Number(crop.value);
    });
    return Object.keys(data).map(key => ({
      x: key,
      y: Math.round(data[key].value),
      unit: '$',
    }));
  };

  /**
   *
   * @function _toggelRevenueChart
   * @memberof RevenueChart
   */
  _toggelRevenueChart = () => {
    this.setState({ showRevenueChart: !this.state.showRevenueChart });
  };
}

const mapDispatchToProps = {
  goToFieldPage: () => push(ROUTE_FIELDS),
};

export default connect(
  null,
  mapDispatchToProps
)(RevenueChart);



// WEBPACK FOOTER //
// ./src/containers/RevenueChart/index.js